import React from 'react';
import { cn } from '../utils';
import { Loader2 } from 'lucide-react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive';
    size?: 'sm' | 'md' | 'lg' | 'icon';
    isLoading?: boolean;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({
        className,
        variant = 'primary',
        size = 'md',
        isLoading = false,
        leftIcon,
        rightIcon,
        children,
        disabled,
        ...props
    }, ref) => {
        const variants = {
            primary: 'bg-primary text-primary-foreground hover:bg-primary-dark shadow-sm shadow-primary/20',
            secondary: 'bg-secondary text-secondary-foreground hover:opacity-90 shadow-sm shadow-secondary/20',
            outline: 'bg-transparent border-2 border-primary/20 text-primary hover:bg-primary/5',
            ghost: 'bg-transparent text-gray-600 hover:bg-gray-100',
            destructive: 'bg-destructive text-destructive-foreground hover:opacity-90 shadow-sm shadow-destructive/20',
        };

        const sizes = {
            sm: 'px-3 py-1.5 text-xs rounded-sm',
            md: 'px-6 py-2.5 text-sm rounded-md',
            lg: 'px-8 py-3 text-base rounded-lg',
            icon: 'p-2 rounded-md',
        };

        return (
            <button
                ref={ref}
                disabled={isLoading || disabled}
                className={cn(
                    'inline-flex items-center justify-center gap-2 font-medium transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none',
                    variants[variant],
                    sizes[size],
                    className
                )}
                {...props}
            >
                {isLoading && <Loader2 size={16} className="animate-spin" />}
                {!isLoading && leftIcon}
                {children}
                {!isLoading && rightIcon}
            </button>
        );
    }
);

Button.displayName = 'Button';
